import type { Idea } from './types';

/**
 * Calculate days since an idea was last watered (or created if never watered)
 * This is the single source of truth for all time-based calculations
 */
export function calculateDaysSinceWatered(idea: Idea): number {
  const referenceDate = idea.last_watered_at 
    ? new Date(idea.last_watered_at) 
    : new Date(idea.created_at);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - referenceDate.getTime());
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Get progress dot count based on days since watered
 * 0-1 days: 1 dot, 2-3 days: 2 dots, 4-6 days: 3 dots, 7+: 4 dots
 */
export function getProgressDots(idea: Idea): number {
  const days = calculateDaysSinceWatered(idea);
  if (days <= 1) return 1;
  if (days <= 3) return 2;
  if (days <= 6) return 3;
  return 4;
}

/**
 * Get human-readable time cue for display
 */
export function getTimeCue(idea: Idea): string {
  const days = calculateDaysSinceWatered(idea);
  const wasWatered = idea.last_watered_at && idea.last_watered_at !== idea.created_at;
  
  if (days === 0) {
    return wasWatered ? 'Returned today' : 'Just planted';
  }
  
  if (days === 1) {
    return wasWatered ? 'You returned yesterday' : 'Quiet for 1 day';
  }
  
  return `Quiet for ${days} days`;
}

/**
 * Check if idea is near withering threshold (5-6 days without activity)
 */
export function isNearWither(idea: Idea): boolean {
  if (idea.growth_stage === 'tree' || idea.status === 'withered') return false;
  const days = calculateDaysSinceWatered(idea);
  return days >= 5 && days < 7;
}

/**
 * Check if idea should be withered (7+ days without activity)
 * Note: This is for display purposes. Actual withering requires a cron job.
 */
export function shouldBeWithered(idea: Idea): boolean {
  if (idea.growth_stage === 'tree') return false;
  return calculateDaysSinceWatered(idea) >= 7;
}
